/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react';
import {useContext} from 'react';
import {Table} from 'semantic-ui-react';
import {useTranslation} from 'react-i18next';
import OrderItemsRich from '../OrderItemsRich/OrderItemsRich';
import {UltyModalWrapperContext} from '../../../../Shared/UltyModalWrapper/UltyModalWrapperContext';
import OrderItemRow from '../OrderItemRow';
import { UltyMoney } from '@ulty/ulty-ui';
import useUser from '../../../../Shared/UserProvider/useUser';


const DiscountPriceRow = ({discountPrice}) => {
    const [t] = useTranslation();
    const {user} = useUser();
    const greenBold = {
        color: 'green',
        fontWeight: 'bold'
    };

    return (
        <Table.Row>
            <Table.HeaderCell />
            <Table.HeaderCell />
            <Table.HeaderCell />

            <Table.HeaderCell textAlign="right" style={greenBold} colSpan='2'>
                {t('order.discount')}
                <UltyMoney
                    readonly
                    amount={discountPrice}
                    currencyCode={user.getCompanyCurrency()}
                    locale={user.getCompanyLocale()}
                />
            </Table.HeaderCell>
        </Table.Row>
    )
}

const OrderItemsTable = ({ order }) => {
    const [t] = useTranslation();
    const {user} = useUser();

    const {handleUltyModalWrapper} = useContext(UltyModalWrapperContext)

    const orderItemsWithFulfillmentIssue = order.orderItems.filter(oi => oi.fulfillmentIssue && oi.fulfillmentIssue !== 'FOUND_ITEM')

    const defineProductViewModal = (items) => {
        const modalSettings = {
            title: t('order.product_details'),
            component: <OrderItemsRich
                order={items}
                afterSubmit={() => handleUltyModalWrapper(false, null)}
            />
        };

        handleUltyModalWrapper(true, modalSettings);
    };

    const sumProducts = () => order.orderItems.reduce((total, orderItem) => total + orderItem.quantity * orderItem.unitPrice, 0);

    const shouldDisplayDiscountPrice = () => {
        return orderItemsWithFulfillmentIssue?.length === 0 && order?.discountPrice > 0;
    }

    return (
            <Table size='small'>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell width={7}>{t('order.num_products', {count: order.nbProducts})}</Table.HeaderCell>
                        <Table.HeaderCell width={5}>{t('order.fulfillment.state')}</Table.HeaderCell>
                        <Table.HeaderCell textAlign="right" width={1}>{t('order.quantity')}</Table.HeaderCell>
                        <Table.HeaderCell textAlign="right" width={2}>{t('order.order_item.unit_price')}</Table.HeaderCell>
                        <Table.HeaderCell textAlign="right" width={1}>{t('order.order_item.total_price')}</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                <Table.Body key={order.id}>
                    {order.orderItems.filter(oi => !oi.fulfillmentIssueParentId).map(oi => {
                        const oiFulfillmentIssue = orderItemsWithFulfillmentIssue.find(fi => fi.id === oi.id)
                        const oiQuantity = oiFulfillmentIssue?.quantity

                        return (
                          <OrderItemRow
                            key={oi.id}
                            order={order}
                            orderItem={oi}
                            onOrderItemDetailsPreview={() => defineProductViewModal(order)}
                            relatedReplacement={order.orderItems.find(orderItem => orderItem.fulfillmentIssueParentId === oi.id)}
                            {...oiQuantity !== undefined ? { currentQuantity: oiQuantity } : {}}
                          />
                        )
                    })}
                </Table.Body>

                <Table.Footer>
                    {shouldDisplayDiscountPrice() &&
                        <DiscountPriceRow discountPrice={order.discountPrice} />
                    }
                    <Table.Row css={css`
                      s, strike {
                        text-decoration: none;
                        position: relative;
                        font-size: .8em;
                      }

                      s::before, strike::before {
                        top: 50%;
                        background: red;
                        opacity: .7;
                        content: '';
                        width: 110%;
                        position: absolute;
                        height: .2em;
                        border-radius: .2em;
                        left: -5%;
                        white-space: nowrap;
                        display: block;
                        transform: rotate(-15deg);
                      }

                      s.straight::before, strike.straight::before {
                        transform: rotate(0deg);
                        left: -1%;
                        width: 102%;
                      }
                    `}>
                        <Table.HeaderCell>{t('order.total')}</Table.HeaderCell>
                        <Table.HeaderCell />
                        <Table.HeaderCell />
                        <Table.HeaderCell />
                        <Table.HeaderCell textAlign="right" style={{
                            color: order.price !== sumProducts() ? 'red' : 'black',
                            fontWeight: 'bold',
                        }}>
                            {order.discountPrice && order.discountPrice > 0 ? (
                                <div css={css`
                                  display: flex;
                                  align-items: center;
                                  justify-content: flex-end;
                                  & > *:not(:last-child) {
                                    margin-right: 1rem !important;
                                  }
                                `}>
                                    <s>
                                        <UltyMoney
                                            readonly
                                            amount={order.price}
                                            currencyCode={user.getCompanyCurrency()}
                                            locale={user.getCompanyLocale()}
                                        />
                                    </s>
                                    <UltyMoney
                                        css={css`margin: 0`}
                                        readonly
                                        amount={order.price - order.discountPrice}
                                        currencyCode={user.getCompanyCurrency()}
                                        locale={user.getCompanyLocale()}
                                    />
                                </div>
                            ) : (
                                <UltyMoney
                                    readonly
                                    amount={order.price}
                                    currencyCode={user.getCompanyCurrency()}
                                    locale={user.getCompanyLocale()}
                                />
                            )}
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Footer>
            </Table>
    )
}

export default OrderItemsTable;
